import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {AllowanceEntity, BokkzTslDataService, Medebewoner, Woning} from '@snelstart-ventures/fiscaal/domain-logic';
import {FormArray, FormControl, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {filter, Observable, shareReplay, take} from 'rxjs';
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {SharedUiDialogModule} from '@snelstart/ui-dialog';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {CommonModule} from '@angular/common';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

type PeriodeFormGroup = FormGroup<{
  begindatum: FormControl<Date | null>;
  einddatum: FormControl<Date | null>;
}>;

type WoningFromGroup = FormGroup<{
  bewoondePeriode: PeriodeFormGroup;
  huisnummer: FormControl<string | null>;
  huisnummerToevoeging: FormControl<string | null>;
  postcode: FormControl<string | null>;
  woonplaats: FormControl<string | null>;
  kaleHuurprijsPerMaand: FormControl<number | null>;
  energiekostenPerMaand: FormControl<number | null>;
  huismeersterkostenPerMaand: FormControl<number | null>;
  algemeneservicePerMaand: FormControl<number | null>;
  schoonmaakkostenPerMaand: FormControl<number | null>;
  kreegHuursubsidieHoogVermogen: FormControl<boolean | null>;
  uitzonderingVoorRekenhuurVanToepassing: FormControl<boolean | null>;
  aangepasteWoning: FormControl<boolean | null>;
  medebewoners: FormArray<MedebewonerFormGroup>;
}>;

type MedebewonerFormGroup = FormGroup<{
  naam: FormControl<string | null>;
  geboortedatum: FormControl<Date | null>;
  bsn: FormControl<string | null>;
  inkomen: FormControl<number | null>;
  toetsvermogen: FormControl<number | null>;
  periodeSamenwonend: PeriodeFormGroup
}>;

@Component({
  selector: 'snelstart-venture-fiscaal-feature-huurtoeslag',
  templateUrl: './huurtoeslag.component.html',
  styleUrls: ['./huurtoeslag.component.scss'],
  imports: [
    SharedUiButtonModule,
    SharedUiDialogModule,
    ReactiveFormsModule,
    SharedUiDetailsModule,
    SharedUiFormControlsModule,
    CommonModule
  ]
})
export class HuurtoeslagComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  private dataService = inject(BokkzTslDataService);
  private destroyRef = inject(DestroyRef);
  public dialogVisible = false;
  public dialogMedebewonerVisible = false;
  public index = 0;
  public year: any;

  public formGroup = new FormGroup({
    huurwoningen: new FormArray<WoningFromGroup>([]),
  });

  readonly allowanceEntity$: Observable<AllowanceEntity> =
    this.dataService.allowanceEntity$.pipe(
      filter((data): data is AllowanceEntity => !!data?.sstRelationId),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  public ngOnInit(): void {
    this.allowanceEntity$.pipe(
      take(1),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((data) => {
      this.year = data.year;
      this.updateForm(data.toeslagen?.huurtoeslag?.huurwoningen ?? []);
    });
  }

  ngOnDestroy(): void {
    this.saveForm();
    this.dataService.calculateLatestAllowances().subscribe();
  }

  showDialog(index: number) {
    this.index = index;
    this.dialogVisible = true;
  }

  showDialogMedebewoner(index: number) {
    this.index = index;
    this.dialogMedebewonerVisible = true;
  }

  get woningen(): FormArray<WoningFromGroup> {
    return this.formGroup.controls.huurwoningen;
  }

  getMedebewoners(index: number): FormArray<MedebewonerFormGroup> {
    return this.woningen.at(index).controls.medebewoners;
  }

  addRij(): void {
    const huurWoning = this.createFormGroup();
    this.woningen.push(huurWoning);
  }

  verwijderRij(index: number): void {
    this.woningen.removeAt(index, {emitEvent: false});
    this.formGroup.updateValueAndValidity({emitEvent: false});
    this.formGroup.markAsDirty({emitEvent: false});
    this.saveForm();
  }

  addMedebewoner(index: number): void {
    const innerGroup = this.createFormGroupMedebewoner();
    const outerGroup = this.woningen.at(index) as FormGroup;
    const innerArray = outerGroup.get('medebewoners') as FormArray;
    innerArray.push(innerGroup);
  }

  verwijderMedebewoner(outerIndex: number, innerIndex: number): void {
    const innerArray = this.getMedebewoners(outerIndex);
    innerArray.removeAt(innerIndex);
    this.formGroup.updateValueAndValidity({emitEvent: false});
    this.formGroup.markAsDirty({emitEvent: false});
    this.saveForm();
  }

  createFormGroup(item?: Woning) {
    return new FormGroup({
      bewoondePeriode: new FormGroup({
        begindatum: new FormControl<Date | null>(item?.bewoondePeriode?.begindatum ? new Date(item.bewoondePeriode.begindatum) : null),
        einddatum: new FormControl<Date | null>(item?.bewoondePeriode?.einddatum ? new Date(item.bewoondePeriode.einddatum) : null),
      }),
      huisnummer: new FormControl<string | null>(item?.huisnummer ?? null),
      huisnummerToevoeging: new FormControl<string | null>(item?.huisnummerToevoeging ?? null),
      postcode: new FormControl<string | null>(this.formatPostcode(item?.postcode) ?? null),
      woonplaats: new FormControl<string | null>(item?.woonplaats ?? null),
      kaleHuurprijsPerMaand: new FormControl<number | null>(item?.kaleHuurprijsPerMaand ?? null),
      energiekostenPerMaand: new FormControl<number | null>(item?.energiekostenPerMaand ?? null),
      huismeersterkostenPerMaand: new FormControl<number | null>(item?.huismeersterkostenPerMaand ?? null),
      algemeneservicePerMaand: new FormControl<number | null>(item?.algemeneservicePerMaand ?? null),
      schoonmaakkostenPerMaand: new FormControl<number | null>(item?.schoonmaakkostenPerMaand ?? null),
      kreegHuursubsidieHoogVermogen: new FormControl<boolean | null>(item?.kreegHuursubsidieHoogVermogen ?? null),
      uitzonderingVoorRekenhuurVanToepassing: new FormControl<boolean | null>(item?.uitzonderingVoorRekenhuurVanToepassing ?? null),
      aangepasteWoning: new FormControl<boolean | null>(item?.aangepasteWoning ?? null),
      medebewoners: new FormArray<MedebewonerFormGroup>((item?.medebewoners ?? []).map(p => this.createFormGroupMedebewoner(p))),
    });
  }

  createFormGroupMedebewoner(item?: Medebewoner) {
    return new FormGroup({
      naam: new FormControl<string | null>(item?.naam ?? null),
      geboortedatum: new FormControl<Date | null>(item?.geboortedatum ?? null),
      bsn: new FormControl<string | null>(item?.bsn ?? null),
      inkomen: new FormControl<number | null>(item?.inkomen ?? null),
      toetsvermogen: new FormControl<number | null>(item?.toetsvermogen ?? null),
      periodeSamenwonend: new FormGroup({
        begindatum: new FormControl<Date | null>(item?.periodeSamenwonend?.begindatum ? new Date(item.periodeSamenwonend.begindatum) : null),
        einddatum: new FormControl<Date | null>(item?.periodeSamenwonend?.einddatum ? new Date(item.periodeSamenwonend.einddatum) : null),
      })
    });
  }

  private updateForm(data: Woning[]): void {
    const fa = this.woningen;

    while (fa.length > data.length) {
      fa.removeAt(fa.length - 1, {emitEvent: false});
    }

    data.forEach((woning, i) => {
      if (woning.postcode) {
        woning.postcode = this.formatPostcode(woning.postcode);
      }

      if (i >= fa.length) {
        fa.push(this.createFormGroup(woning), {emitEvent: false});
        return;
      }

      const woningGroup = fa.at(i);
      woningGroup.patchValue(
        {
          huisnummer: woning.huisnummer ?? null,
          huisnummerToevoeging: woning.huisnummerToevoeging ?? null,
          postcode: woning.postcode ?? null,
          woonplaats: woning.woonplaats ?? null,
          kaleHuurprijsPerMaand: woning.kaleHuurprijsPerMaand ?? null,
          energiekostenPerMaand: woning.energiekostenPerMaand ?? null,
          huismeersterkostenPerMaand: woning.huismeersterkostenPerMaand ?? null,
          algemeneservicePerMaand: woning.algemeneservicePerMaand ?? null,
          schoonmaakkostenPerMaand: woning.schoonmaakkostenPerMaand ?? null,
          kreegHuursubsidieHoogVermogen: woning.kreegHuursubsidieHoogVermogen ?? null,
          uitzonderingVoorRekenhuurVanToepassing: woning.uitzonderingVoorRekenhuurVanToepassing ?? null,
          aangepasteWoning: woning.aangepasteWoning ?? null,
          bewoondePeriode: {
            begindatum: woning.bewoondePeriode?.begindatum ? new Date(woning.bewoondePeriode.begindatum) : null,
            einddatum: woning.bewoondePeriode?.einddatum ? new Date(woning.bewoondePeriode.einddatum) : null,
          },
        },
        {emitEvent: false},
      );

      const medebewoners = woning.medebewoners ?? [];
      const medebewonersFa = woningGroup.controls.medebewoners;

      while (medebewonersFa.length > medebewoners.length) {
        medebewonersFa.removeAt(medebewonersFa.length - 1, {emitEvent: false});
      }

      medebewoners.forEach((medebewoner, j) => {
        if (j < medebewonersFa.length) {
          medebewonersFa.at(j).patchValue(
            {
              naam: medebewoner.naam ?? null,
              geboortedatum: medebewoner.geboortedatum ? new Date(medebewoner.geboortedatum) : null,
              bsn: medebewoner.bsn ?? null,
              inkomen: medebewoner.inkomen ?? null,
              toetsvermogen: medebewoner.toetsvermogen ?? null,
              periodeSamenwonend: {
                begindatum: medebewoner.periodeSamenwonend?.begindatum ? new Date(medebewoner.periodeSamenwonend.begindatum) : null,
                einddatum: medebewoner.periodeSamenwonend?.einddatum ? new Date(medebewoner.periodeSamenwonend.einddatum) : null,
              },
            },
            {emitEvent: false},
          );
        } else {
          medebewonersFa.push(this.createFormGroupMedebewoner(medebewoner), {emitEvent: false});
        }
      });
    });
  }

  saveForm(): void {
    this.formGroup.updateValueAndValidity({emitEvent: false});

    setTimeout(() => {
      const edited = this.formGroup.getRawValue().huurwoningen ?? [];

      this.dataService.patchAllowanceEntity((currentEntity) => {
        const existing = currentEntity.toeslagen?.huurtoeslag?.huurwoningen ?? [];

        const mergedWoningen: Woning[] = edited.map((editedWoning, i) => {
          const existingWoning = existing[i] ?? ({} as Woning);

          const editedMedebewoners = editedWoning.medebewoners ?? [];
          const existingMedebewoners = existingWoning.medebewoners ?? [];

          const mergedMedebewoners: Medebewoner[] = editedMedebewoners.map((editedM, j) => {
            const baseM =
              (editedM.bsn && existingMedebewoners.find(e => e.bsn && e.bsn === editedM.bsn)) ??
              existingMedebewoners[j] ??
              ({} as Medebewoner);

            const cleanedM = this.stripNulls(editedM) as Record<string, unknown>;
            if (editedM.periodeSamenwonend) {
              cleanedM['periodeSamenwonend'] = this.stripNulls(editedM.periodeSamenwonend);
            }
            return {...baseM, ...cleanedM} as Medebewoner;
          });

          const cleanedWoning = this.stripNulls(editedWoning) as Record<string, unknown>;
          if (editedWoning.bewoondePeriode) {
            cleanedWoning['bewoondePeriode'] = this.stripNulls(editedWoning.bewoondePeriode);
          }
          cleanedWoning['medebewoners'] = mergedMedebewoners;

          return {...existingWoning, ...cleanedWoning} as Woning;
        });

        return {
          ...currentEntity,
          toeslagen: {
            ...currentEntity.toeslagen,
            huurtoeslag: {
              ...currentEntity.toeslagen?.huurtoeslag,
              huurwoningen: mergedWoningen,
            },
          },
        };
      });
    }, 0);
  }

  private stripNulls<T extends object>(value: T): Partial<T> {
    return Object.fromEntries(
      Object.entries(value).filter(([, v]) => v !== null),
    ) as Partial<T>;
  }

  formatPostcode(postcode: string | null | undefined): string {
    if (!postcode) return ''; // Retourneer een lege string als postcode null of undefined is

    // Verwijder alle niet-alfanumerieke tekens (bijv. spaties)
    const cleanedPostcode = postcode.replace(/[^a-zA-Z0-9]/g, '');

    // Zorg voor 4 cijfers en 2 hoofdletters
    if (cleanedPostcode.length >= 4) {
      const cijfers = cleanedPostcode.substring(0, 4).replace(/\D/g, ''); // Alleen cijfers
      const letters = cleanedPostcode.substring(4, 6).replace(/[^a-zA-Z]/g, '').toUpperCase(); // Alleen letters
      return `${cijfers}${letters}`;
    }

    return cleanedPostcode; // Als de postcode te kort is, retourneer de schoongemaakte versie
  }
}
